import { mapState } from 'vuex';
import { isEmpty, isEqual, cloneDeep } from 'lodash';
import Sortable from 'sortablejs';
const DEFAULT_SELECTED_COLUMNS = ['_time', '_value', 'unit', 'dtwin_id', 'dtwin_title', 'state_id', 'state_identifier'];
// Keep in sync with BE: backend/unify-service/services/background-runner/methods/csvExportConstants.js
const EXCLUDED_FROM_CLUSTERED = ['_value', 'unit', 'state_title'];
const BASIC_HEADERS_VD = [{
  name: '_time',
  disabled: true
}, {
  name: '_value',
  disabled: true
}, {
  name: 'unit'
}, {
  name: 'dtwin_id'
}, {
  name: 'dtwin_title'
}, {
  name: 'state_id'
}, {
  name: 'state_identifier'
}, {
  name: 'state_title'
}, {
  name: 'external_id'
}, {
  name: 'twin_ancestor_ids'
}, {
  name: 'twin_tags'
}, {
  name: 'vdevice_groups'
}, {
  name: 'operational_status'
}, {
  name: 'connector_id'
}, {
  name: 'address'
}, {
  name: 'latitude'
}, {
  name: 'longitude'
}];
const BASIC_HEADERS_DT = [{
  name: '_time',
  disabled: true
}, {
  name: '_value',
  disabled: true
}, {
  name: 'unit'
}, {
  name: 'dtwin_id'
}, {
  name: 'dtwin_title'
}, {
  name: 'state_id'
}, {
  name: 'state_identifier'
}, {
  name: 'state_title'
}, {
  name: 'twin_ancestor_ids'
}, {
  name: 'twin_tags'
}, {
  name: 'address'
}, {
  name: 'latitude'
}, {
  name: 'longitude'
}];
export default {
  name: 'CoreCsvExportFormattingOptions',
  props: {
    value: {
      type: Object,
      default: () => ({
        headers: [],
        includeHeaders: true
      })
    },
    readonly: {
      type: Boolean,
      default: false
    },
    filterType: {
      type: String,
      required: true,
      default: 'digitalTwin'
    },
    customProperties: {
      type: Array,
      required: false,
      default: () => []
    },
    references: {
      type: Array,
      required: false,
      default: () => []
    },
    selectedDriversVariables: {
      type: Array,
      required: false,
      default: () => []
    },
    devicesCount: {
      type: Number,
      required: false,
      default: -1
    },
    driversCount: {
      type: Number,
      required: false,
      default: -1
    }
  },
  data() {
    return {
      internalModel: {
        ...this.value
      },
      headers: [],
      includeHeaders: true
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'color']),
    staticHeaders() {
      const basicHeaders = this.filterType === 'digitalTwin' ? BASIC_HEADERS_DT : BASIC_HEADERS_VD;
      const clustered = Boolean(this.internalModel.clusterPerPacket);
      return [...basicHeaders.filter(h => !clustered || !EXCLUDED_FROM_CLUSTERED.includes(h.name)).map(h => ({
        ...h
      })), ...this.customProperties.map(cp => ({
        name: `${cp.keyValue}`,
        count: cp.count,
        isCustomProperty: true
      })), ...this.references.map(ref => ({
        name: `${ref.key}`,
        count: ref.count,
        isReferenceProperty: true
      })), ...(clustered ? this.driversVariableHeaders() : [])];
    },
    themeMode() {
      return Boolean(this.$store.state.app.UiTheme) ? 'dark' : 'light';
    },
    tableLocalizations() {
      return {
        headers: [{
          text: '',
          sortable: false
        }, {
          text: this.$i18n_t('BackgroundJobs.ScheduledExports.dialog.csvFormattingOptions.position'),
          sortable: false
        }, {
          text: this.$i18n_t('BackgroundJobs.ScheduledExports.dialog.csvFormattingOptions.originalColumnName'),
          sortable: false
        }, {
          text: this.$i18n_t('BackgroundJobs.ScheduledExports.dialog.csvFormattingOptions.newColumnName'),
          sortable: false
        }]
      };
    },
    mapValuesRules() {
      return [v => !/[,;]/.test(v) || this.$i18n_t('Common.commasNotAllowed'), v => !this.headers.find(f => {
        return f.name === v;
      }) || this.$i18n_t('Common.uniqueValue')];
    }
  },
  watch: {
    internalModel: {
      handler() {
        this.emitInput();
      },
      deep: true
    },
    value: {
      handler(newValue) {
        if (!isEqual(newValue, this.internalModel)) {
          this.internalModel = {
            ...this.value
          };
        }
      },
      deep: true
    },
    filterType() {
      this.headers = this.defaultHeaders();
    },
    'internalModel.clusterPerPacket'() {
      this.headers = this.defaultHeaders();
    },
    selectedDriversVariables() {
      this.headers = this.headers.filter(h => !h.isInParsedState).concat(this.driversVariableHeaders());
    },
    headers() {
      this.initSortableTable();
      this.emitInput();
    },
    includeHeaders() {
      this.emitInput();
    }
  },
  mounted() {
    this.headers = isEmpty(this.internalModel.headers) ? this.defaultHeaders() : cloneDeep(this.internalModel.headers);
    if (this.internalModel.includeHeaders !== undefined) {
      this.includeHeaders = this.internalModel.includeHeaders;
    }
    this.initSortableTable();
  },
  methods: {
    initSortableTable() {
      const table = document.querySelector('.states-table tbody');
      Sortable.create(table, {
        handle: '.handle',
        onEnd: ({
          newIndex,
          oldIndex
        }) => {
          this.headers.splice(newIndex, 0, ...this.headers.splice(oldIndex, 1));
          this.emitInput();
        }
      });
    },
    emitInput() {
      this.$emit('input', {
        headers: this.headers,
        includeHeaders: this.includeHeaders,
        clusterPerPacket: this.internalModel.clusterPerPacket,
        formIsValid: this.includeHeaders ? !this.headers.some(el => {
          return el.mappedValue && /[,;]/.test(el.mappedValue);
        }) && !this.headers.some(element => {
          return this.headers.some(el => el.mappedValue && element.name === el.mappedValue);
        }) : true
      });
    },
    defaultHeaders() {
      return this.staticHeaders.filter(sh => DEFAULT_SELECTED_COLUMNS.includes(sh.name)).concat(this.driversVariableHeaders());
    },
    driversVariableHeaders() {
      return this.selectedDriversVariables.map(variable => ({
        name: `${variable.title}`,
        count: variable.count,
        isInParsedState: true
      }));
    },
    getHeaderOptionString(item) {
      let itemText = '';
      let itemVariableText = '';
      if (item.isCustomProperty) {
        itemText = ` (${item.count} ${this.$i18n_t('Common.of')} ${this.devicesCount} ${this.$i18n_t(`BackgroundJobs.ScheduledExports.dialog.data.${this.filterType === 'digitalTwin' ? 'digitalTwins' : 'virtualDevices'}`)})`;
      } else if (item.isReferenceProperty) {
        itemVariableText = ` (${item.count} ${this.$i18n_t('Common.of')} ${this.devicesCount} ${this.$i18n_t('DigitalTwin.VirtualDevice.wizard.referenceIds')})`;
      } else if (item.isInParsedState) {
        itemVariableText = ` (${item.count} ${this.$i18n_t('Common.of')} ${this.driversCount} ${this.$i18n_t('BackgroundJobs.ScheduledExports.dialog.data.deviceDrivers')})`;
      }
      return `${item.name}${itemText}${itemVariableText}`;
    }
  }
};